import { useEffect } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { setPassword } from '@lootboy/lib/actions/UserActions';
import {
  EChangePasswordFormField,
  useChangePasswordForm,
} from '@lootboy/lib/forms/useChangePasswordForm';
import { NOOP } from '@lootboy/lib/utils';

import Alert from '~/components/Alert';
import { PasswordInput } from '~/components/atoms/FormControls/PasswordInput';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';
import { ValidationHints } from '~/components/molecules/ValidationHints';
import { passwordValidationSchema } from '~/components/molecules/ValidationHints/validationSchema';

import { userHasPasswordSelector } from '~/redux/selectors/user';
import { previousPathSelector } from '~/redux/slices/location/selectors';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import EnsureLoggedInContainer from '~/routes/EnsureLoggedInRoute/EnsureLoggedInContainer';
import { USER_DETAILS_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useAppHistory } from '~/utilities/routing';

import styles from './ChangePassword.module.scss';

const CHANGE_PASSWORD_FORM_ID = 'change_password_form';

export function ChangePasswordPopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const history = useAppHistory();
  const previousPath = useSelector(previousPathSelector);

  // this will be false if user had signed up using a social provider and has not set their password
  const doesUserHavePassword = useSelector(userHasPasswordSelector);

  const goBack = () => {
    if (previousPath === USER_DETAILS_PATH) {
      history.goBack();
    } else {
      history.push(USER_DETAILS_PATH);
    }
  };

  const {
    register,
    onFormSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    isSubmitEnabled,
  } = useChangePasswordForm(
    async (values) => {
      try {
        await dispatch(setPassword(values.oldPassword, values.newPassword));
        alert.success(
          <Alert
            body={I18n.t('ResetPasswordView_reset_succesful')}
            title={I18n.t('common_success')}
          />,
        );
        goBack();
      } catch (error) {
        const { message } = parseLootBoyError(error);
        alert.error(
          <Alert
            title={I18n.t('common_error')}
            body={message}
          />,
        );
      }
    },
    { isOldPasswordRequired: doesUserHavePassword },
  );

  const newPassword = watch(EChangePasswordFormField.NEW_PASSWORD);
  const newPasswordRepeat = watch(EChangePasswordFormField.REPEAT_NEW_PASSWORD);

  useEffect(() => {
    if (newPassword && newPasswordRepeat) {
      trigger(EChangePasswordFormField.REPEAT_NEW_PASSWORD).catch(NOOP);
    }
  }, [newPassword, newPasswordRepeat, trigger]);

  return (
    <EnsureLoggedInContainer>
      <MenuViewHeader
        title={
          doesUserHavePassword
            ? I18n.t('MenuView_change_password')
            : I18n.t('ChangePasswordView_header_set')
        }
        onBack={goBack}
      />
      <ScrollContainer>
        <section className={styles.container}>
          {doesUserHavePassword ? (
            <h2 className={styles.heading}>
              {I18n.t('MenuView_reset_headline')}
            </h2>
          ) : (
            <AuthViewHeadline
              headline={I18n.t('MenuView_reset_headline')}
              subHeadline={I18n.t('MenuView_reset_sub')}
            />
          )}
          <form
            onSubmit={onFormSubmit}
            className={styles.form}
            id={CHANGE_PASSWORD_FORM_ID}
          >
            {doesUserHavePassword && (
              <PasswordInput
                {...register(EChangePasswordFormField.OLD_PASSWORD)}
                error={errors[EChangePasswordFormField.OLD_PASSWORD]?.message}
                id={EChangePasswordFormField.OLD_PASSWORD}
                label={I18n.t(
                  'ChangePasswordView_placeholder_current_password',
                )}
                placeholder={I18n.t(
                  'PasswordLoginViewNew_placeholder_enter_password',
                )}
                autoComplete="current-password"
              />
            )}
            <PasswordInput
              {...register(EChangePasswordFormField.NEW_PASSWORD)}
              error={errors[EChangePasswordFormField.NEW_PASSWORD]?.message}
              id={EChangePasswordFormField.NEW_PASSWORD}
              label={I18n.t('MenuView_reset_setpassword')}
              placeholder={I18n.t('MenuView_reset_setpassword_placeholder')}
              autoComplete="new-password"
            />
            <PasswordInput
              {...register(EChangePasswordFormField.REPEAT_NEW_PASSWORD)}
              error={
                errors[EChangePasswordFormField.REPEAT_NEW_PASSWORD]?.message
              }
              id={EChangePasswordFormField.REPEAT_NEW_PASSWORD}
              label={I18n.t('MenuView_reset_setpassword_repeat')}
              placeholder={I18n.t('MenuView_reset_repeat_placeholder')}
              autoComplete="new-password"
            />
          </form>
          <ValidationHints
            constraints={passwordValidationSchema}
            value={newPassword}
          />
        </section>
      </ScrollContainer>
      <footer className={styles.footer}>
        <StyledButton
          type="submit"
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          disabled={!isSubmitEnabled || !isValid}
          form={CHANGE_PASSWORD_FORM_ID}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </EnsureLoggedInContainer>
  );
}
